import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useApuracaoSN } from '@/contexts';
import { useSegregacaoFiltros } from '../../useSegregacaoFiltros';
import { TotaisGerais } from './TotaisGerais';
import { ReceitaInternaTabs } from './ReceitaInternaTabs';
import { ReceitasTable } from './ReceitasTable';
import { ImpostoInternoTabs } from './ImpostoInternoTabs';
import { ImpostosTable } from './ImpostosTable';
import { NgrTable } from './NgrTable';
import { ProdutosIgnoradosTable } from './ProdutosIgnoradosTable';
import { ProdutosEmRevisaoTable } from './ProdutosEmRevisaoTable';
import { ProdutosSegregadosTable } from './ProdutosSegregadosTable';
import { DetalhesLinhaModal } from './DetalhesLinhaModal';
import { StatusProdutosChart } from './StatusProdutosChart';
import { ProdutosFiltradosTable } from './ProdutosFiltradosTable';
import { AlterarTipoItemDialog } from './AlterarTipoItemDialog';
import { AlterarSegmentoDialog } from './AlterarSegmentoDialog';
import { ExportarSegregacaoButton } from './ExportarSegregacaoButton';
import { XMLsConvertidosDialog } from './XMLsConvertidosDialog';
import type {
  SegregacaoDetalharPayload,
  XmlConvertidoPayload,
} from '@/services/SimplesNacional/Segregacao/api/types';

function SectionCard({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className="p-0 pb-2">{children}</CardContent>
    </Card>
  );
}

export function SegregacaoDetalhadaSection() {
  const { segregacao, isLoading } = useApuracaoSN();
  const calcularPayload = useSegregacaoFiltros();

  // Modal de detalhe de linha
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLinha, setSelectedLinha] = useState<string | null>(null);

  // Gráfico de status + tabela filtrada
  const [statusFiltro, setStatusFiltro] = useState<string | null>(null);
  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([]);
  const [tipoItemOpen, setTipoItemOpen] = useState(false);
  const [segmentoOpen, setSegmentoOpen] = useState(false);
  const [xmlsOpen, setXmlsOpen] = useState(false);

  const detalharPayload: Omit<SegregacaoDetalharPayload, 'LinhaSegregada'> | null = calcularPayload
    ? {
        CNPJ_Contribuinte: calcularPayload.CnpjContribuinte,
        Ano: calcularPayload.Ano,
        Mes: calcularPayload.Mes,
      }
    : null;

  const xmlsPayload: XmlConvertidoPayload | null = calcularPayload
    ? {
        CnpjContribuinte: calcularPayload.CnpjContribuinte,
        Ano: calcularPayload.Ano,
        Mes: calcularPayload.Mes,
      }
    : null;

  const handleClickLinha = (linha: string) => {
    setSelectedLinha(linha);
    setModalOpen(true);
  };

  const handleClickStatus = (status: string | null) => {
    setStatusFiltro(status);
    setProdutosSelecionados([]);
  };

  const handleLoteSuccess = () => {
    setProdutosSelecionados([]);
    setStatusFiltro(null);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Carregando segregação...</span>
        </CardContent>
      </Card>
    );
  }

  if (!segregacao) return null;

  const tot = segregacao.TOT?.[0];

  return (
    <>
      <div className="space-y-4">
        {/* Cabeçalho da seção com botão de exportar */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Segregação Detalhada
          </h2>
          <ExportarSegregacaoButton />
        </div>

        {/* Totais Gerais */}
        <TotaisGerais
          tot={tot}
          action={
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setXmlsOpen(true)}
            >
              XMLs Convertidos
            </Button>
          }
        />

        {/* Gráfico de Status dos Produtos */}
        <SectionCard
          title="Produtos por Status"
          action={
            produtosSelecionados.length > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {produtosSelecionados.length} selecionado(s)
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setTipoItemOpen(true)}
                >
                  Alterar Tipo Item
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setSegmentoOpen(true)}
                >
                  Alterar Segmento
                </Button>
              </div>
            ) : undefined
          }
        >
          <div className="px-4 pb-2 pt-1">
            <StatusProdutosChart
              segregacao={segregacao}
              statusSelecionado={statusFiltro}
              onClickStatus={handleClickStatus}
            />
          </div>
        </SectionCard>

        {/* Tabela de produtos filtrados pelo gráfico */}
        <SectionCard title={statusFiltro ? `Produtos — Status Filtrado` : 'Todos os Produtos'}>
          <ProdutosFiltradosTable
            segregacao={segregacao}
            statusFiltro={statusFiltro}
            selected={produtosSelecionados}
            onSelectionChange={setProdutosSelecionados}
          />
        </SectionCard>

        {/* Receita Interna */}
        <SectionCard title="Receita Interna">
          <div className="px-4 pb-2">
            <ReceitaInternaTabs
              ris={segregacao.RIS ?? []}
              ric={segregacao.RIC ?? []}
              onClickLinha={handleClickLinha}
            />
          </div>
        </SectionCard>

        {/* Revenda para Exterior */}
        <SectionCard title="Revenda para Exterior (REX)">
          <ReceitasTable data={segregacao.REX ?? []} onClickLinha={handleClickLinha} />
        </SectionCard>

        {/* Imposto Interno */}
        <SectionCard title="Imposto Interno">
          <div className="px-4 pb-2">
            <ImpostoInternoTabs
              iis={segregacao.IIS ?? []}
              iic={segregacao.IIC ?? []}
              onClickLinha={handleClickLinha}
            />
          </div>
        </SectionCard>

        {/* Imposto Externo */}
        <SectionCard title="Imposto Externo (IEX)">
          <ImpostosTable data={segregacao.IEX ?? []} onClickLinha={handleClickLinha} />
        </SectionCard>

        {/* Não Gerador de Receita */}
        <SectionCard title="Não Gerador de Receita (NGR)">
          <NgrTable data={segregacao.NGR ?? []} />
        </SectionCard>

        {/* Produtos Ignorados */}
        <SectionCard title="Produtos Ignorados">
          <ProdutosIgnoradosTable data={segregacao.IGN ?? []} />
        </SectionCard>

        {/* Produtos em Revisão */}
        <SectionCard title="Produtos em Revisão">
          <ProdutosEmRevisaoTable data={segregacao.REV ?? []} />
        </SectionCard>

        {/* Produtos Segregados */}
        <SectionCard title="Produtos Segregados">
          <ProdutosSegregadosTable data={segregacao.SEG ?? []} />
        </SectionCard>
      </div>

      <DetalhesLinhaModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        payload={detalharPayload}
        linha={selectedLinha}
      />

      <AlterarTipoItemDialog
        open={tipoItemOpen}
        onOpenChange={setTipoItemOpen}
        produtos={produtosSelecionados}
        onSuccess={handleLoteSuccess}
      />

      <AlterarSegmentoDialog
        open={segmentoOpen}
        onOpenChange={setSegmentoOpen}
        produtos={produtosSelecionados}
        onSuccess={handleLoteSuccess}
      />

      <XMLsConvertidosDialog open={xmlsOpen} onOpenChange={setXmlsOpen} payload={xmlsPayload} />
    </>
  );
}
